<script setup lang="ts">
import { DTextField } from 'front-library'
import { validateTranslate } from '@/app/core/composable/general'
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { UseValidation } from '@/app/core/composable/validation/useValidation'

interface FieldGroup {
  name: string
  label: string
  disabled: boolean
  type: string
  placeholder: string
  hint: string
  required: boolean
  rule: string
  mask: string
  min: number
  max: number
  minlength: number
  maxlength: number
}

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  validation: {
    type: Object as () => UseValidation,
    required: false,
    default: () => ({})
  },
  field: {
    type: Object as () => { name: string; label: string },
    required: true
  },
  allModel: {
    type: Object,
    required: true,
    default: () => ({})
  },
  class: { type: String, default: '' },
  error: { type: String, default: '' },
  options: {
    type: Array<FieldGroup>
  }
})

const { model, validationSchema, handleValidation, translateErrorMessage } =
  props.validation

const { t } = useI18n()
const localValidationSchema = ref<any>({})
const isInitialized = ref(false)

const handleUpdateField = (name: string, value: any) => {
  const array = model[props.field.name] as any[]

  if (!Array.isArray(array)) {
    model[props.field.name] = []
  }

  const targetObject = array.find(
    element =>
      element && typeof element === 'object' && element.hasOwnProperty(name)
  )

  if (targetObject) {
    targetObject[name] = value
  } else {
    const newObject = { [name]: value }
    array.push(newObject)
  }

  handleValidation()
}

const settingOptions = (options: FieldGroup[]) => {
  let fields: { [key: string]: any } = {}
  options.forEach(field => {
    const rules: string[] = []

    if (field.rule) rules.push(field.rule)
    if (field.minlength) rules.push('minlength:' + field.minlength)
    if (field.maxlength) rules.push('maxlength:' + field.maxlength)
    if (field.required) rules.push('required')

    fields[field.name] = {
      isValid: false,
      errors: [],
      rules,
      ignoreField: false
    }
  })

  const key = Object.keys(validationSchema).find(key =>
    key.includes(props.field.name)
  )
  if (key) {
    validationSchema[key].fields = fields
    localValidationSchema.value = validationSchema[key]
  }

  reorderModelByOptions(options)
}

const reorderModelByOptions = (options: FieldGroup[]) => {
  const currentArray = model[props.field.name] as any[]
  if (!Array.isArray(currentArray)) {
    model[props.field.name] = []
    return
  }

  if (!isInitialized.value && currentArray.length === 0) {
    const initialArray: any[] = []
    options.forEach((option, index) => {
      initialArray[index] = { [option.name]: '' }
    })
    model[props.field.name] = initialArray
    isInitialized.value = true
    return
  }

  if (currentArray.length > 0) {
    const reorderedArray: any[] = []
    options.forEach((option, index) => {
      const existingObject = currentArray.find(
        item =>
          item && typeof item === 'object' && item.hasOwnProperty(option.name)
      )

      if (existingObject) {
        reorderedArray[index] = existingObject
      }
    })

    if (reorderedArray.some(item => item)) {
      model[props.field.name] = reorderedArray
    }
  }
}

watch(
  () => props.options,
  newVal => settingOptions(newVal as FieldGroup[]),
  { immediate: true }
)

const getField = (field: FieldGroup) => {
  const array = model[props.field.name] as any[]
  if (!Array.isArray(array)) {
    return ''
  }

  for (const element of array) {
    if (
      element &&
      typeof element === 'object' &&
      element.hasOwnProperty(field.name)
    ) {
      return element[field.name]
    }
  }

  return ''
}
</script>

<template>
  <template v-for="(field, index) in props.options" :key="index">
    <DTextField
      :label="field.name"
      :disabled="field.disabled"
      :modelValue="getField(field)"
      :type="field.type"
      :placeholder="
        field.placeholder || validateTranslate('form.placeholder', [field.name])
      "
      @update:model-value="handleUpdateField(field.name, $event)"
      :hint="field.hint"
      :required="field.required"
      :mask="field.mask"
      :min="field.min"
      :max="field.max"
      :minlength="field.minlength"
      :maxlength="field.maxlength"
      :class="props.class"
      :error="
        translateErrorMessage(
          t,
          localValidationSchema.fields[field.name]?.errors
        )
      " />
  </template>
</template>
